// FILE: src/App.js
import React, { Suspense } from "react";
import { Routes, Route, Link, Navigate } from "react-router-dom";

/* ===========================
   USER-FACING PAGES
=========================== */
import Home from "./pages/Home";
import Discover from "./pages/Discover";
import Results from "./pages/Results";
import CartPage from "./pages/CartPage";
import SignInRequired from "./pages/SignInRequired";
import SignIn from "./pages/SignIn";
import UserSignUp from "./pages/UserSignUp";
import MyBookings from "./pages/MyBookings";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DeleteAccount from "./pages/DeleteAccount";
import RideNow from "./pages/RideNow";

/* ✅ Owner payout account page (NEW) */
import OwnerPayoutAccount from "./pages/OwnerPayoutAccount";

/* ===========================
   OWNER PAGES
=========================== */
import ManageDrivers from "./pages/ManageDrivers";
import OwnerVehicleDocuments from "./pages/OwnerVehicleDocuments";
import OwnerLanding from "./pages/OwnerLanding";
import VehicleOwnerSignIn from "./pages/VehicleOwnerSignIn";
import VehicleOwnerSignUp from "./pages/VehicleOwnerSignUp";
import ManageCars from "./pages/ManageCars";
import OwnerVehicleForm from "./pages/OwnerVehicleForm";
import ManageBookings from "./pages/ManageBookings";
import EarningsDashboard from "./pages/EarningsDashboard";
import OwnerSecurity from "./pages/OwnerSecurity";
import OwnerProfile from "./pages/OwnerProfile";

/* ===========================
   AUTH / MISC
=========================== */
import AuthLanding from "./pages/AuthLanding";
import UserDashboard from "./pages/UserDashboard";

/* ===========================
   REVIEW / MARKETING / PROFILE
=========================== */
import VehicleReview from "./pages/VehicleReview";
import AboutWayroot from "./pages/AboutWayroot";
import ProfilePage from "./pages/ProfilePage";

/* ===========================
   ADMIN
=========================== */
import AdminLayout from "./admin/components/AdminLayout";
import RequireAdmin from "./admin/components/RequireAdmin";
import AdminLogin from "./admin/pages/AdminLogin";
import AdminSecurity from "./admin/pages/AdminSecurity";
import AdminAudit from "./admin/pages/AdminAudit";
import AdminMapsUsageLogs from "./admin/pages/AdminMapsUsageLogs";
import AdminPlaceholder from "./admin/pages/AdminPlaceholder";

import AdminComplianceQueue from "./admin/pages/compliance/vehicles/AdminComplianceQueue";
import AdminComplianceDetail from "./admin/pages/compliance/vehicles/AdminComplianceDetail";
import AdminComplianceLayout from "./admin/pages/compliance/AdminComplianceLayout";
import ComplianceHome from "./admin/pages/compliance/ComplianceHome";
import AdminDriverComplianceQueue from "./admin/pages/compliance/drivers/AdminDriverComplianceQueue";
import AdminDriverComplianceDetail from "./admin/pages/compliance/drivers/AdminDriverComplianceDetail";
import AdminFinancePayments from "./admin/pages/finance/AdminFinancePayments";
import AdminTransactions from "./admin/pages/transactions/AdminTransactions";
import AdminTrackerLayout from "./admin/pages/tracker/AdminTrackerLayout";
import AdminOwnerTrackerPage from "./admin/pages/tracker/AdminOwnerTrackerPage";
import AdminPhoneTrackerPage from "./admin/pages/tracker/AdminPhoneTrackerPage";

/* ✅ Operations */
import AdminOpsLayout from "./admin/pages/operations/AdminOpsLayout";
import OpsDashboard from "./admin/pages/operations/OpsDashboard";
import OpsLiveTrips from "./admin/pages/operations/OpsLiveTrips";
import OpsIncidents from "./admin/pages/operations/OpsIncidents";
import OpsPolicies from "./admin/pages/operations/OpsPolicies";
import OpsReports from "./admin/pages/operations/OpsReports";
import OpsBookings from "./admin/pages/operations/OpsBookings";
import OpsRides from "./admin/pages/operations/OpsRides";
import OpsRequests from "./admin/pages/operations/OpsRequests";

/* ✅ Support */
import AdminSupportLayout from "./admin/pages/support/AdminSupportLayout";
import SupportHome from "./admin/pages/support/SupportHome";
import SupportEntityIndex from "./admin/pages/support/SupportEntityIndex";
import SupportEntityDetailLayout from "./admin/pages/support/SupportEntityDetailLayout";
import SupportHelpDesk from "./admin/pages/support/SupportHelpDesk";

/* Support Tabs (Users generic) */
import SupportOverviewTab from "./admin/pages/support/tabs/SupportOverviewTab";
import SupportRequestsTab from "./admin/pages/support/tabs/SupportRequestsTab";
import SupportBookingsTab from "./admin/pages/support/tabs/SupportBookingsTab";
import SupportPaymentsTab from "./admin/pages/support/tabs/SupportPaymentsTab";
import SupportNotesTab from "./admin/pages/support/tabs/SupportNotesTab";

/* ✅ Owner Support (Dedicated layout + tabs) */
import SupportOwnerLayout from "./admin/pages/support/owners/SupportOwnerLayout";
import SupportOwnerOverview from "./admin/pages/support/owners/tabs/SupportOwnerOverview";
import SupportOwnerVehiclesTab from "./admin/pages/support/owners/tabs/SupportOwnerVehiclesTab";
import SupportOwnerInboxTab from "./admin/pages/support/owners/tabs/SupportOwnerInboxTab";
import SupportOwnerPayoutsTab from "./admin/pages/support/owners/tabs/SupportOwnerPayoutsTab";
import SupportOwnerNotesTab from "./admin/pages/support/owners/tabs/SupportOwnerNotesTab";
import SupportOwnerBookingsTab from "./admin/pages/support/owners/tabs/SupportOwnerBookingsTab";
import SupportOwnerRidesTab from "./admin/pages/support/owners/tabs/SupportOwnerRidesTab";

/* ✅ Driver Support (Dedicated layout + tabs) */
import SupportDriverLayout from "./admin/pages/support/drivers/SupportDriverLayout";
import SupportDriverOverview from "./admin/pages/support/drivers/tabs/SupportDriverOverview";
import SupportDriverBookingsTab from "./admin/pages/support/drivers/tabs/SupportDriverBookingsTab";
import SupportDriverRidesTab from "./admin/pages/support/drivers/tabs/SupportDriverRidesTab";
import SupportDriverNotesTab from "./admin/pages/support/drivers/tabs/SupportDriverNotesTab";

/* ✅ Directory (NEW) */
import AdminDirectoryLayout from "./admin/pages/directory/AdminDirectoryLayout";
import DirectoryHome from "./admin/pages/directory/DirectoryHome";
import DirectoryUsers from "./admin/pages/directory/DirectoryUsers";
import DirectoryOwners from "./admin/pages/directory/DirectoryOwners";
import DirectoryDrivers from "./admin/pages/directory/DirectoryDrivers";
import DirectoryVehicles from "./admin/pages/directory/DirectoryVehicles";

/* ===========================
   CONTEXTS
=========================== */
import { SearchProvider } from "./context/SearchContext";
import { UserProvider, RequireAuth, ROLES } from "./context/UserContext";

import Toaster from "./components/ui/Toaster";
import RouteSeo from "./seo/RouteSeo";

const OwnerVehicleTypeChooser = React.lazy(() => import("./pages/OwnerVehicleTypeChooser"));
const OwnerRideNowVehicleChooser = React.lazy(() => import("./pages/OwnerRideNowVehicleChooser"));
const OwnerAutoErikshawForm = React.lazy(() => import("./pages/OwnerAutoErikshawForm"));
const AdminPricingConsole = React.lazy(() => import("./admin/pages/AdminPricingConsole"));
const AdminMarketingConsole = React.lazy(() => import("./admin/pages/AdminMarketingConsole"));
const AdminMarketingLayout = React.lazy(() => import("./admin/pages/AdminMarketingLayout"));
const AdminMarketingReports = React.lazy(() => import("./admin/pages/AdminMarketingReports"));
const AdminMarketingDiagnostics = React.lazy(() => import("./admin/pages/AdminMarketingDiagnostics"));
const RequirementsInbox = React.lazy(() => import("./admin/pages/RequirementsInbox"));
const OpsTripRequests = React.lazy(() => import("./admin/pages/operations/OpsTripRequests"));
const OpsTripBookings = React.lazy(() => import("./admin/pages/operations/OpsTripBookings"));
const OpsTripSetup = React.lazy(() => import("./admin/pages/operations/OpsTripSetup"));
const OpsAppVersionControl = React.lazy(() => import("./admin/pages/operations/OpsAppVersionControl"));
const OpsTripRequestDetail = React.lazy(() => import("./admin/pages/operations/OpsTripRequestDetail"));
const OpsScheduledCatalog = React.lazy(() => import("./admin/pages/operations/OpsScheduledCatalog"));
const OpsFlowGuide = React.lazy(() => import("./admin/pages/operations/OpsFlowGuide"));
const AdminVendorsWorkspace = React.lazy(() => import("./admin/pages/vendors/AdminVendorsWorkspace"));
const AdminVendorConsentDocument = React.lazy(() => import("./admin/pages/vendors/AdminVendorConsentDocument"));

/* ===========================
   NOT FOUND
=========================== */
function NotFound() {
  return (
    <div className="min-h-screen grid place-items-center bg-white">
      <div className="text-center">
        <h1 className="text-3xl font-bold">404</h1>
        <p className="mt-1 text-slate-600">Page not found</p>
        <Link to="/" className="inline-block mt-4 px-4 py-2 rounded-xl bg-black text-white">
          Go home
        </Link>
      </div>
    </div>
  );
} 

function RouteFallback() {
  return (
    <div className="min-h-[40vh] grid place-items-center bg-white text-slate-600">
      Loading...
    </div>
  );
}

function LazyRoute({ children }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

export default function App() {
  return (
    <UserProvider>
      <SearchProvider>
        <RouteSeo />
        <Routes>
          {/* ===========================
              PUBLIC
          =========================== */}
          <Route path="/" element={<Home />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/results" element={<Results />} />
          <Route path="/results-preview" element={<Navigate to="/results" replace />} />
          <Route path="/search" element={<Navigate to="/discover" replace />} />
          <Route path="/explore" element={<Navigate to="/discover" replace />} />
          <Route path="/about-wayroot" element={<AboutWayroot />} />
          <Route path="/ride-now" element={<RideNow />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          {/* ===========================
              ADMIN
          =========================== */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* ✅ If anyone hits /admin directly, always land on Operations */}
          <Route path="/admin" element={<Navigate to="/admin/ops" replace />} />

          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            }
          >
            {/* ✅ No dashboard anymore: index routes to Operations */}
            <Route index element={<Navigate to="ops" replace />} />

            {/* ✅ Directory */}
            <Route path="directory" element={<AdminDirectoryLayout />}>
              <Route index element={<DirectoryHome />} />
              <Route path="users" element={<DirectoryUsers />} />
              <Route path="owners" element={<DirectoryOwners />} />
              <Route path="drivers" element={<DirectoryDrivers />} />
              <Route path="vehicles" element={<DirectoryVehicles />} />
              <Route path="vendors" element={<LazyRoute><AdminVendorsWorkspace title="Vendors Directory" subtitle="Clean directory view for vendor profile, linked supply cars, pending scheduled offers, accepted cases and alignment history." /></LazyRoute>} />
            </Route>

            {/* ✅ Operations module */}
            <Route path="ops" element={<AdminOpsLayout />}>
              <Route index element={<OpsDashboard />} />
              <Route path="flow-guide" element={<LazyRoute><OpsFlowGuide /></LazyRoute>} />
              <Route path="requests" element={<OpsRequests />} />
              <Route path="trip-requests" element={<LazyRoute><OpsTripRequests /></LazyRoute>} />
              <Route path="trip-bookings" element={<LazyRoute><OpsTripBookings /></LazyRoute>} />
              <Route path="trip-setup" element={<LazyRoute><OpsTripSetup /></LazyRoute>} />
              <Route path="app-version" element={<LazyRoute><OpsAppVersionControl /></LazyRoute>} />
              <Route path="trip-setup/catalog" element={<LazyRoute><OpsScheduledCatalog /></LazyRoute>} />
              <Route path="trip-requests/catalog" element={<Navigate to="/admin/ops/trip-setup/catalog" replace />} />
              <Route path="trip-requests/:id" element={<LazyRoute><OpsTripRequestDetail /></LazyRoute>} />
              <Route path="vendor-consents/:vendorId/:vehicleId" element={<LazyRoute><AdminVendorConsentDocument /></LazyRoute>} />
              <Route path="vendors" element={<LazyRoute><AdminVendorsWorkspace title="Operations Vendors" subtitle="Operations view for outsourced supply. Track vendor readiness, linked cars, accepted requests, admin alignment and recent scheduled movement." compactHeader /></LazyRoute>} />
              <Route path="bookings" element={<OpsBookings />} />
              <Route path="rides" element={<OpsRides />} />
              <Route path="live" element={<OpsLiveTrips />} />
              <Route path="incidents" element={<OpsIncidents />} />
              <Route path="policies" element={<OpsPolicies />} />
              <Route path="reports" element={<OpsReports />} />
            </Route>

            <Route path="transactions" element={<AdminTransactions />} />
            <Route path="finance" element={<AdminFinancePayments />} />
            <Route path="pricing" element={<LazyRoute><AdminPricingConsole /></LazyRoute>} />
            <Route path="marketing" element={<LazyRoute><AdminMarketingLayout /></LazyRoute>}>
              <Route index element={<LazyRoute><AdminMarketingConsole /></LazyRoute>} />
              <Route path="reports" element={<LazyRoute><AdminMarketingReports /></LazyRoute>} />
              <Route path="diagnostics" element={<LazyRoute><AdminMarketingDiagnostics /></LazyRoute>} />
            </Route>
            <Route path="requirements" element={<LazyRoute><RequirementsInbox /></LazyRoute>} />
            <Route path="logs" element={<AdminMapsUsageLogs />} />

            <Route path="tracker" element={<AdminTrackerLayout />}>
              <Route index element={<Navigate to="owner-cases" replace />} />
              <Route path="owner-cases" element={<AdminOwnerTrackerPage />} />
              <Route path="phone-members" element={<AdminPhoneTrackerPage />} />
            </Route>

            {/* Compliance */}
            <Route path="compliance" element={<AdminComplianceLayout />}>
              <Route index element={<ComplianceHome />} />
              <Route path="vehicles" element={<AdminComplianceQueue />} />
              <Route path="vehicles/:regNo" element={<AdminComplianceDetail />} />
              <Route path="drivers" element={<AdminDriverComplianceQueue />} />
              <Route path="drivers/:driverId" element={<AdminDriverComplianceDetail />} />
            </Route>

            {/* ✅ Support */}
            <Route path="support" element={<AdminSupportLayout />}>
              <Route index element={<SupportHome />} />
              <Route path="help" element={<SupportHelpDesk />} />
              <Route
                path="vendors"
                element={
                  <LazyRoute>
                    <AdminVendorsWorkspace
                      title="Vendor Support"
                      subtitle="Support view for outsourced supply. Track vendor profile, simple vendor cars, scheduled requests, created bookings and recent linked case movement in one place."
                      compactHeader
                    />
                  </LazyRoute>
                }
              />

              {/* ✅ Dedicated layouts MUST come before the generic :entityType/:entityId route */}
              <Route path="owners/:ownerId" element={<SupportOwnerLayout />}>
                <Route path="overview" element={<SupportOwnerOverview />} />
                <Route path="vehicles" element={<SupportOwnerVehiclesTab />} />
                <Route path="inbox" element={<SupportOwnerInboxTab />} />
                <Route path="bookings" element={<SupportOwnerBookingsTab />} />
                <Route path="rides" element={<SupportOwnerRidesTab />} />
                <Route path="payouts" element={<SupportOwnerPayoutsTab />} />
                <Route path="notes" element={<SupportOwnerNotesTab />} />
                <Route index element={<Navigate to="overview" replace />} />
              </Route>

              <Route path="drivers/:driverId" element={<SupportDriverLayout />}>
                <Route path="overview" element={<SupportDriverOverview />} />
                <Route path="bookings" element={<SupportDriverBookingsTab />} />
                <Route path="rides" element={<SupportDriverRidesTab />} />
                <Route path="notes" element={<SupportDriverNotesTab />} />
                <Route index element={<Navigate to="overview" replace />} />
              </Route>

              {/* Search lists: /admin/support/users | /admin/support/owners | /admin/support/drivers */}
              <Route path=":entityType" element={<SupportEntityIndex />} />

              {/* ✅ Generic detail layout (users primarily; kept for compatibility) */}
              <Route path=":entityType/:entityId" element={<SupportEntityDetailLayout />}>
                <Route path="overview" element={<SupportOverviewTab />} />
                <Route path="requests" element={<SupportRequestsTab />} />
                <Route path="bookings" element={<SupportBookingsTab />} />
                <Route path="payments" element={<SupportPaymentsTab />} />
                <Route path="notes" element={<SupportNotesTab />} />

                {/* Not used for users/drivers (kept for layout consistency) */}
                <Route path="vehicles" element={<AdminPlaceholder title="Vehicles" />} />
                <Route path="inbox" element={<AdminPlaceholder title="Inbox" />} />
                <Route path="payouts" element={<AdminPlaceholder title="Payouts" />} />

                <Route index element={<Navigate to="overview" replace />} />
              </Route>
            </Route>

            <Route path="security" element={<AdminSecurity />} />
            <Route path="audit" element={<AdminAudit />} />
          </Route>

          {/* aliases */}
          <Route path="/admin-panel" element={<Navigate to="/admin/ops" replace />} />
          <Route path="/admin-panel/login" element={<Navigate to="/admin/login" replace />} />

          {/* ===========================
              OWNER
          =========================== */}
          <Route path="/owners/onboard" element={<OwnerLanding />} />

          {/* Owner dashboard */}
          <Route
            path="/owner/dashboard"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <OwnerLanding />
              </RequireAuth>
            }
          />

          {/* Owner auth routes */}
          <Route path="/vehicle-owner/vehicle-owner-signin" element={<VehicleOwnerSignIn />} />
          <Route path="/vehicle-owner/vehicle-owner-signup" element={<VehicleOwnerSignUp />} />
          <Route path="/vehicle-owner-signin" element={<Navigate to="/vehicle-owner/vehicle-owner-signin" replace />} />
          <Route path="/vehicle-owner-signup" element={<Navigate to="/vehicle-owner/vehicle-owner-signup" replace />} />

          {/* ✅ Owner payout account page */}
          <Route
            path="/owner/account"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <OwnerPayoutAccount />
              </RequireAuth>
            }
          />

          <Route
            path="/owner/cars"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <ManageCars />
              </RequireAuth>
            }
          />
          <Route
            path="/owner/drivers"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <ManageDrivers />
              </RequireAuth>
            }
          />
          <Route
            path="/owner/cars/new"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <LazyRoute>
                  <OwnerVehicleTypeChooser />
                </LazyRoute>
              </RequireAuth>
            }
          />
          <Route
            path="/owner/cars/new/car"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <OwnerVehicleForm />
              </RequireAuth>
            }
          />
          <Route
            path="/owner/cars/new/ride-now"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <LazyRoute>
                  <OwnerRideNowVehicleChooser />
                </LazyRoute>
              </RequireAuth>
            }
          />
          <Route
            path="/owner/cars/new/auto-erikshaw"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <LazyRoute>
                  <OwnerAutoErikshawForm />
                </LazyRoute>
              </RequireAuth>
            }
          />
          <Route
            path="/owner/cars/:registrationNumber/edit"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <OwnerVehicleForm />
              </RequireAuth>
            }
          />
          <Route
            path="/owner/cars/:registrationNumber/edit/auto-erikshaw"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <LazyRoute>
                  <OwnerAutoErikshawForm />
                </LazyRoute>
              </RequireAuth>
            }
          />
          <Route
            path="/owner/cars/:registrationNumber/documents"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <OwnerVehicleDocuments />
              </RequireAuth>
            }
          />
          <Route
            path="/owner/bookings"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <ManageBookings />
              </RequireAuth>
            }
          />
          <Route
            path="/owner/earnings"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <EarningsDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/owner/security"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <OwnerSecurity />
              </RequireAuth>
            }
          />
          <Route
            path="/owner/profile"
            element={
              <RequireAuth roles={[ROLES.OWNER]} redirectTo="/vehicle-owner/vehicle-owner-signin">
                <OwnerProfile />
              </RequireAuth>
            }
          />

          {/* ===========================
              USER AUTH
          =========================== */}
          <Route path="/login" element={<SignIn />} />
          <Route path="/signup" element={<UserSignUp />} />
          <Route path="/auth" element={<AuthLanding />} />

          <Route
            path="/dashboard"
            element={
              <RequireAuth redirectTo="/signin-required">
                <UserDashboard />
              </RequireAuth>
            }
          />
          <Route path="/user-dashboard" element={<Navigate to="/dashboard" replace />} />

          <Route
            path="/cart"
            element={
              <RequireAuth redirectTo="/signin-required">
                <CartPage />
              </RequireAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth redirectTo="/signin-required">
                <ProfilePage />
              </RequireAuth>
            }
          />
          <Route
            path="/reviews/vehicle"
            element={
              <RequireAuth redirectTo="/signin-required">
                <VehicleReview />
              </RequireAuth>
            }
          />

          <Route path="/signin-required" element={<SignInRequired />} />
          <Route path="/my-bookings" element={<MyBookings />} />

          {/* ===========================
              404
          =========================== */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        <Toaster />
      </SearchProvider>
    </UserProvider>
  );
}
